import { useEffect, useState } from "react";
import { stripNotificationNavigationIntent } from "@/lib/navigation-intent";
import { initSystemLog, setSystemLogZero } from "@/lib/system-log";
import { mutators } from "@/lib/mutators";
import { usePresenceTracking } from "@/hooks/use-presence-tracking";

interface MembershipLike {
  id: string;
  channel_id: string;
  hidden: boolean;
}

interface UserLike {
  id: string;
}

interface UseAppCrossCuttingEffectsArgs {
  user: UserLike | null | undefined;
  loading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  zero: any;
  selectedChannel: string | null;
  myMemberships: ReadonlyArray<MembershipLike> | undefined;
}

/**
 * The grab-bag of small cross-cutting effects that used to inline at
 * the top of `AppInner`:
 *   - Strip notification navigation intent from the URL on mount.
 *   - Track user activity for presence (heartbeat + auto-away).
 *   - Wire `setSystemLogZero` + `initSystemLog` once the user resolves.
 *   - Unhide a DM when the viewer explicitly navigates to it.
 *   - 15s bootstrap timeout for the "loading is taking too long" screen.
 */
export function useAppCrossCuttingEffects({
  user,
  loading,
  zero,
  selectedChannel,
  myMemberships,
}: UseAppCrossCuttingEffectsArgs) {
  useEffect(() => {
    const cleaned = stripNotificationNavigationIntent(window.location.href);
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (cleaned !== current) {
      window.history.replaceState({}, "", cleaned);
    }
  }, []);

  // Track user activity for presence (heartbeat + auto-away)
  usePresenceTracking();

  useEffect(() => {
    if (user) {
      setSystemLogZero(
        zero as unknown as Parameters<typeof setSystemLogZero>[0],
      );
      initSystemLog(user.id);
    }
  }, [user, zero]);

  // Unhide DM when navigating to it (via search, command palette, new DM flow).
  // The DM query filters by hidden=false, so hidden DMs don't appear in the
  // sidebar. When a user explicitly navigates to one, unhide it so it reappears.
  useEffect(() => {
    if (!selectedChannel) return;
    const membership = (myMemberships ?? []).find(
      (m) => m.channel_id === selectedChannel && m.hidden,
    );
    if (membership) {
      void zero.mutate(
        mutators.channel_members.update({ id: membership.id, hidden: false }),
      );
    }
  }, [selectedChannel, myMemberships, zero]);

  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false);
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => setBootstrapTimedOut(true), 15_000);
    return () => clearTimeout(timer);
  }, [loading]);

  return { bootstrapTimedOut };
}
